import { FileVideo, Loader2, Upload, Video, X } from "lucide-react";
import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { showToastError } from "../../shared/alertService";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import Textarea from "../ui/Textarea";

/** Limitações de upload (alinhadas ao backend: upload_video.py) */
const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-matroska",
  "video/webm",
];
const ALLOWED_VIDEO_EXT = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
const MAX_VIDEO_SIZE_MB = 200;
const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;
const ACCEPT_VIDEO = ".mp4,.mov,.avi,.mkv,.webm";

interface VideoUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: () => void;
  loadingUpload: boolean;
  postId: number | null;
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  destination: string;
  setDestination: (value: string) => void;
  caption: string;
  setCaption: (value: string) => void;
}

const VideoUploadModal: React.FC<VideoUploadModalProps> = ({
  open,
  onClose,
  onUpload,
  loadingUpload,
  postId,
  selectedFile,
  setSelectedFile,
  destination,
  setDestination,
  caption,
  setCaption,
}) => {
  const { t } = useTranslation();
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = (file: File) => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    const typeOk =
      ALLOWED_VIDEO_TYPES.includes(file.type) ||
      ALLOWED_VIDEO_EXT.includes(ext);
    if (!typeOk) {
      showToastError(
        t(
          "Formato não suportado ({name}). Use: MP4, MOV, AVI, MKV ou WebM. Tamanho máx.: {max}MB.",
          { name: file.name, max: MAX_VIDEO_SIZE_MB },
        ),
      );
      return;
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      showToastError(
        t("Arquivo muito grande ({size}MB). Tamanho máximo: {max}MB.", {
          size: sizeMB,
          max: MAX_VIDEO_SIZE_MB,
        }),
      );
      return;
    }
    setSelectedFile(file);

    // Criar preview do vídeo
    const reader = new FileReader();
    reader.onloadend = () => {
      setVideoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    } else {
      setSelectedFile(null);
      setVideoPreview(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Upload de Vídeo - Post #{postId}", { postId: postId || "" })}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loadingUpload}
            size="sm"
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="button"
            variant="primary"
            icon={
              loadingUpload ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )
            }
            onClick={onUpload}
            disabled={loadingUpload || !selectedFile}
            size="sm"
          >
            {loadingUpload ? t("Enviando...") : t("Enviar Vídeo")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="space-y-3">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
              {t("Selecione um vídeo")}
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer bg-white",
                isDragging
                  ? "border-blue-500 bg-blue-50 scale-[1.02]"
                  : "border-blue-300 hover:border-blue-400",
              )}
            >
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept={ACCEPT_VIDEO}
                  onChange={handleFileChange}
                  className="hidden"
                  aria-label={t("Upload de vídeo")}
                  disabled={loadingUpload}
                />
                <div className="flex flex-col items-center gap-2">
                  <Video
                    className={cn(
                      "h-8 w-8 transition-colors",
                      isDragging ? "text-blue-600" : "text-blue-500",
                    )}
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      {t("Clique para selecionar")}
                    </span>
                    <span className="text-xs text-gray-500 block mt-1">
                      {t("ou arraste e solte um vídeo aqui")}
                    </span>
                  </div>
                  <span
                    className="text-[10px] text-gray-500"
                    title={t("Limitações do servidor")}
                  >
                    {t("MP4, MOV, AVI, MKV, WebM. Tamanho máx.: {max}MB", {
                      max: MAX_VIDEO_SIZE_MB,
                    })}
                  </span>
                </div>
              </label>
            </div>
          </div>

          {selectedFile && (
            <div className="mt-3 p-3 rounded-lg border border-purple-200 bg-gray-50/50">
              <div className="flex items-start gap-3">
                {videoPreview && (
                  <div className="shrink-0 relative">
                    <video
                      src={videoPreview}
                      className="w-32 h-20 object-cover rounded-lg border border-purple-200"
                      muted
                      preload="metadata"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                      <Video className="h-6 w-6 text-white" />
                    </div>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <FileVideo className="h-4 w-4 text-blue-600 shrink-0" />
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selectedFile.name}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(selectedFile.size)} • {selectedFile.type}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null);
                    setVideoPreview(null);
                  }}
                  className="shrink-0 p-1 text-gray-400 hover:text-red-600 transition-colors"
                  disabled={loadingUpload}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <Select
            label={t("Destino")}
            value={destination}
            onChange={(value) => setDestination(value)}
            options={[
              { value: "instagram_feed", label: "Instagram Feed" },
              { value: "instagram_reels", label: "Instagram Reels" },
              { value: "facebook_reels", label: "Facebook Reels" },
              { value: "linkedin_feed", label: t("LinkedIn (Feed/Página)") },
              {
                value: "facebook_page",
                label: t("Facebook (Página - Feed)"),
              },
            ]}
            size="md"
            disabled={loadingUpload}
          />
          <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700 flex items-start gap-1.5">
              <span className="font-semibold">💡 {t("Dica:")}</span>
              <span>
                {t(
                  "Para Reels, use formato 1080x1920 (9:16), codec H.264 + AAC para melhor compatibilidade.",
                )}
              </span>
            </p>
          </div>
          <Textarea
            label={t("Legenda")}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={t("Digite a legenda ou descrição do vídeo...")}
            inputSize="sm"
          />
          <p className="text-xs text-gray-500">
            {t(
              "As hashtags do post serão usadas automaticamente nos metadados do vídeo.",
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default VideoUploadModal;
